










































































































import * as R from 'ramda';
import { Component, Vue } from 'vue-property-decorator';
import { startUserDeletionWorkflow } from '@/appsync/lambda/startUserDeletionWorkflowService';
import { getIsPortalEnabledStatus } from '@/appsync/service/SystemSettingQueryService';
import { fetchUsers } from '@/appsync/service/UserQueryService';
import DeleteUserDataDialog from '@/components/organisms/DeleteUserDataDialog.vue';
import UserDetailDialog from '@/components/organisms/UserDetailDialog.vue';
import { refreshStores } from '@/containers/globals/globalSharedService';
import { i18n } from '@/plugins/i18n/i18n';
import { assignRoleToUser, listRoles } from '@/services/role/roleService';
import { commonModule } from '@/store/modules/CommonStore';
import { globalSnackbarModule } from '@/store/modules/GlobalSnackbarStore';
import { loginUserModule } from '@/store/modules/LoginUserStore';
import { LoginInfo, LoginNameAndUserRole, UserInfo } from '@/types/type';
import { UserDetail } from '@/utils/UserDetail';
import {
  UserRoleTypes,
  updateUserRole,
  callCheckStepFunctionStatus,
} from '@/utils/UserManagementUtils';
import { SNACKBAR_TIMEOUT } from '@/utils/consts';

@Component({
  components: {
    DeleteUserDataDialog,
    UserDetailDialog,
  },
})
export default class UserManagement extends Vue {
  private isPageLoading: boolean = true;
  private showEditDialog: boolean = false;
  private showDeleteUserDataDialog: boolean = false;
  private isSaved: boolean = false;
  private snackbarTimeout: number = SNACKBAR_TIMEOUT;
  private areUserDetailsLoaded: boolean = false;
  private isLoadingUserDetails: boolean = false;
  private isPortalEnabled: boolean = true;
  private rolesMap: { [key: string]: string } = {};

  private focussedUser: UserDetail = new UserDetail('', UserRoleTypes.General);

  private headers: any[] = [
    {
      text: i18n.tc('commonSetting.userManagement.userNameHeader'),
      align: 'start',
      sortable: false,
      value: 'loginName',
      divider: true,
      width: 512,
    },
    {
      text: i18n.tc('commonSetting.userManagement.roleHeader'),
      align: 'center',
      sortable: false,
      value: 'roleName',
      divider: true,
    },
    {
      text: '',
      align: 'end',
      sortable: false,
      value: 'actions',
      divider: true,
      width: 96,
      class: 'action-class',
    },
  ];

  private users: UserDetail[] = [];
  private search: string = '';
  private searchQueryText: string = '';

  public async created(): Promise<void> {
    const loginInfo: LoginInfo = commonModule.loginInfo;
    await refreshStores(loginInfo);

    // isPortalEnabledを取得
    try {
      this.isPortalEnabled = await getIsPortalEnabledStatus(
        loginInfo.tenant,
        loginInfo.landscape
      );
    } catch (error) {
      this.isPortalEnabled = true; // デフォルト値
    }

    // ロール一覧は常に取得（一覧表示で名称が必要なため）
    try {
      const roles = await listRoles(loginInfo.tenant, loginInfo.landscape);
      this.rolesMap = roles.reduce((acc: Record<string, string>, r: any) => {
        acc[r.id] = r.name;
        return acc;
      }, {});
    } catch (e) {
      this.rolesMap = {}; // 失敗時は空
    }

    this.isPageLoading = false;
  }

  private formatUsersForDisplay(users: LoginNameAndUserRole[]): UserDetail[] {
    return users.map(
      user => new UserDetail(user.loginName, user.userRole, user.roleId)
    );
  }

  private get userInfo(): UserInfo {
    return loginUserModule.userInfo;
  }

  private get loginInfo(): LoginInfo {
    return commonModule.loginInfo;
  }

  private editUser(user: UserDetail) {
    this.focussedUser = user;
    this.showEditDialog = true;
  }

  private deleteUser(user: UserDetail) {
    this.focussedUser = user;
    this.showDeleteUserDataDialog = true;
  }

  private async deleteUserData(loginName: string) {
    this.showDeleteUserDataDialog = false;
    globalSnackbarModule.showSnackbarAction(
      i18n.tc('commonSetting.userManagement.deletionProcessStarted')
    );
    globalSnackbarModule.showProgressCircularAction();
    try {
      const executionArn = await startUserDeletionWorkflow(
        this.loginInfo.tenant,
        this.loginInfo.landscape,
        loginName
      );
      await callCheckStepFunctionStatus(executionArn);
    } catch (error) {
      globalSnackbarModule.showSnackbarAction(
        i18n.tc('commonSetting.userManagement.deletionProcessFailed')
      );
      globalSnackbarModule.hideProgressCircularAction();
    } finally {
      await this.cleanupAfterDeletion();
    }
  }

  private async cleanupAfterDeletion() {
    setTimeout(
      () => globalSnackbarModule.hideSnackbarAction(),
      SNACKBAR_TIMEOUT
    );
    globalSnackbarModule.resetExecutionArnAction();
  }

  private async updateUserRole(user: UserDetail) {
    await updateUserRole(
      user.loginName,
      this.loginInfo.tenant,
      this.loginInfo.landscape,
      user.userRole
    );
  }

  private reflectChangesInUI(updatedUser: UserDetail) {
    const updatedUserIndexedByLoginName = R.indexBy(R.prop('loginName'), [
      updatedUser,
    ]);
    const usersIndexedByLoginName = R.indexBy(R.prop('loginName'), this.users);
    const mergedUsersIndexedByLoginName = R.mergeLeft(
      updatedUserIndexedByLoginName,
      usersIndexedByLoginName
    );
    this.users = R.values(mergedUsersIndexedByLoginName);
  }

  private async updateUserAndReflectChangesInUI(updatedUser: UserDetail) {
    this.showEditDialog = false;
    try {
      // ユーザーロール（Admin/General）を更新
      await this.updateUserRole(updatedUser);

      // 詳細ロール（roleId）が設定されている場合は更新
      if (updatedUser.roleId && updatedUser.roleId !== null) {
        await assignRoleToUser(
          updatedUser.loginName,
          this.loginInfo.tenant,
          this.loginInfo.landscape,
          updatedUser.roleId
        );
      }

      this.reflectChangesInUI(updatedUser);
      this.isSaved = true;
    } catch (error) {
      // エラーハンドリング - 詳細なエラー情報を表示
      let errorMessage = 'ユーザー情報の更新に失敗しました';
      if (error && error.errors && error.errors.length > 0) {
        errorMessage += ': ' + error.errors[0].message;
      }
      globalSnackbarModule.showSnackbarAction(errorMessage);
    }
  }

  private async loadUserDetailsIfNotLoadedAndUpdateSearchQueryText() {
    if (!this.isLoadingUserDetails && !this.areUserDetailsLoaded) {
      this.isLoadingUserDetails = true;

      const users = await fetchUsers(commonModule.loginInfo);
      const formattedUsers = this.formatUsersForDisplay(users);
      this.users.push(...formattedUsers);

      this.isLoadingUserDetails = false;
      this.areUserDetailsLoaded = true;
    }

    this.searchQueryText = this.search;
  }

  private isSameAsLoggedInUser(loginName: string) {
    return loginName === this.loginInfo.loginName;
  }

  // 追加: roleId から名称取得
  private getRoleName(roleId: string | undefined): string {
    if (!roleId) return '';
    return this.rolesMap[roleId] || roleId;
  }

  private get displayUsers() {
    return this.users.map(u => ({
      loginName: u.loginName, // テーブル表示用ユーザ名
      roleName: this.getRoleName(u.roleId || undefined), // 変換後ロール名称
      roleId: u.roleId, // 参照用に保持
      userRole: u.userRole,
    }));
  }
}
